import { useState } from 'react';
import { api } from '../../lib/api';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Loader2, Lock, Mail, Eye, EyeOff } from 'lucide-react';

export const LoginScreen = ({ onSuccess }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) return;
    setLoading(true);
    setError('');
    try {
      const res = await api.post('/auth/login', { email, password });
      const data = res.data;
      const user = data.user || data;
      const token = data.token || user.token;
      localStorage.setItem('auth_token', token);
      sessionStorage.setItem('patron_auth', 'true');
      sessionStorage.setItem('user_role', user.role);
      sessionStorage.setItem('user_email', user.email);
      sessionStorage.setItem('user_nom', user.nom || '');
      onSuccess(user);
    } catch (err) {
      const msg = err.response?.data?.detail || 'Erreur de connexion';
      setError(msg);
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-[rgb(var(--lm-bg))] flex items-center justify-center p-4" data-testid="login-screen">
      <div className="w-full max-w-sm">
        {/* Logo */}
        <div className="text-center mb-8">
          <img src="/logo-carte.jpg" alt="Le Mas" className="h-20 mx-auto mb-4 object-contain" />
          <h1 className="text-xl font-title text-[rgb(var(--lm-text))] tracking-wide">ESPACE GESTION</h1>
          <p className="text-xs text-[rgb(var(--lm-text-muted))] mt-1">Restaurant Le Mas</p>
        </div>

        {/* Login Form */}
        <form onSubmit={handleSubmit} className="lm-card p-6 space-y-4" data-testid="login-form">
          <div className="flex items-center gap-2 mb-4">
            <Lock size={16} className="text-[rgb(var(--lm-gold))]" />
            <span className="text-xs tracking-[0.15em] uppercase text-[rgb(var(--lm-text-muted))]">Connexion securisee</span>
          </div>

          {error && (
            <div className="bg-[rgb(var(--lm-red)/0.1)] border border-[rgb(var(--lm-red)/0.2)] text-[rgb(var(--lm-red))] text-xs px-3 py-2 rounded-lg" data-testid="login-error">
              {error}
            </div>
          )}

          <div className="space-y-1">
            <label className="text-[10px] tracking-[0.1em] uppercase text-[rgb(var(--lm-text-muted))]">Email</label>
            <div className="relative">
              <Mail size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[rgb(var(--lm-text-muted))]" />
              <Input
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="votre@email.com"
                className="pl-9 bg-[rgb(var(--lm-surface))] border-[rgb(var(--lm-border))] text-sm h-10"
                autoFocus
                data-testid="login-email"
              />
            </div>
          </div>

          <div className="space-y-1">
            <label className="text-[10px] tracking-[0.1em] uppercase text-[rgb(var(--lm-text-muted))]">Mot de passe</label>
            <div className="relative">
              <Lock size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[rgb(var(--lm-text-muted))]" />
              <Input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={e => setPassword(e.target.value)}
                placeholder="Mot de passe"
                className="pl-9 pr-10 bg-[rgb(var(--lm-surface))] border-[rgb(var(--lm-border))] text-sm h-10"
                data-testid="login-password"
              />
              <button type="button" onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-[rgb(var(--lm-text-muted))] hover:text-[rgb(var(--lm-text))]">
                {showPassword ? <EyeOff size={14} /> : <Eye size={14} />}
              </button>
            </div>
          </div>

          <Button type="submit" disabled={loading || !email || !password}
            className="w-full bg-[rgb(var(--lm-gold))] text-[rgb(var(--lm-bg))] hover:opacity-90 h-10 text-sm font-medium"
            data-testid="login-submit">
            {loading ? <Loader2 size={16} className="animate-spin mr-2" /> : null}
            Se connecter
          </Button>
        </form>

        <p className="text-center text-[9px] text-[rgb(var(--lm-text-muted)/0.4)] mt-6">
          Saint-Laurent-de-la-Salanque &middot; Pays Catalan
        </p>
      </div>
    </div>
  );
};

export default LoginScreen;
